import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useProduct, useTag, useTeam } from '@react/hooks';
import type { CreateTagRequest, TagVisibility } from '@core/types/tag';
import type { Entity } from '@core/types/entity';
import { Button, Card, CardContent, ErrorAlert, Input } from '@react/components';
import { ColorPicker } from '@/components/ColorPicker';
import { Select } from '@/components/Select';

const COLORS = ['#0ea5a4', '#3b82f6', '#8b5cf6', '#ec4899', '#f97316', '#eab308', '#22c55e', '#64748b'];

export const TagCreatePage = observer(() => {
  const navigate = useNavigate();
  const store = useTag();
  const teams = useTeam();
  const products = useProduct();
  const { t } = useI18n();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [icon, setIcon] = useState('');
  const [visibility, setVisibility] = useState<TagVisibility>('TAG_VISIBILITY_TENANT');
  const [teamID, setTeamID] = useState('');
  const [productID, setProductID] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    void teams.ListTeams().catch(() => {});
    void products.ListProducts().catch(() => {});
  }, [teams, products]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || (visibility === 'TAG_VISIBILITY_TEAM' && !teamID)) return;
    setIsSubmitting(true);
    const request: CreateTagRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      color,
      icon: icon.trim() || undefined,
      visibility,
      ...(visibility === 'TAG_VISIBILITY_TEAM' ? { scope_team: { id: teamID } as Entity } : {}),
      ...(productID ? { scope_product_id: productID } : {}),
    };
    try {
      await store.CreateTag(request);
      const tab = visibility === 'TAG_VISIBILITY_TENANT' ? 'tenant'
        : visibility === 'TAG_VISIBILITY_TEAM' ? 'team' : 'personal';
      navigate(`/tags?visibility=${tab}`);
    } catch {
      // Store exposes the server error in ErrorAlert.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <Link to="/tags" className="text-sm text-muted-foreground hover:text-foreground">{t('tag.backToTags')}</Link>
        <h1 className="mt-3 text-xl font-semibold text-foreground">{t('tag.create')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('tag.createDescription')}</p>
      </div>
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input label={t('tag.name')} value={name} onChange={event => setName(event.target.value)} maxLength={100} required />
            <Input label={t('tag.description')} value={description} onChange={event => setDescription(event.target.value)} />
            <div>
              <span className="mb-2 block text-sm font-medium text-foreground">{t('tag.color')}</span>
              <ColorPicker options={COLORS.map(value => ({ value, label: value }))} value={color} onChange={setColor} />
            </div>
            <Input label={t('tag.iconOptional')} value={icon} onChange={event => setIcon(event.target.value)} maxLength={16} />
            <Select
              label={t('tag.visibility')}
              value={visibility}
              onChange={event => { setVisibility(event.target.value as TagVisibility); setTeamID(''); }}
              options={[
                { value: 'TAG_VISIBILITY_TENANT', label: t('tag.tenant') },
                { value: 'TAG_VISIBILITY_TEAM', label: t('tag.team') },
                { value: 'TAG_VISIBILITY_PERSONAL', label: t('tag.personal') },
              ]}
              helperText={t('tag.visibilityHelp')}
            />
            {visibility === 'TAG_VISIBILITY_TEAM' && (
              <Select
                label={t('tag.scopeTeam')}
                value={teamID}
                onChange={event => setTeamID(event.target.value)}
                placeholder={t('tag.selectTeam')}
                options={teams.items.filter(team => team.entity?.id).map(team => ({ value: team.entity!.id, label: team.name }))}
                helperText={teams.error ? t('tag.teamsLoadError') : undefined}
                required
              />
            )}
            <Select
              label={t('tag.scopeProduct')}
              value={productID}
              onChange={event => setProductID(event.target.value)}
              options={[
                { value: '', label: t('tag.allProducts') },
                ...products.items.filter(product => product.entity?.id).map(product => ({ value: product.entity!.id, label: product.name })),
              ]}
              helperText={t('tag.scopeProductHelp')}
            />
            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <Link to="/tags"><Button type="button" variant="secondary">{t('common.cancel')}</Button></Link>
              <Button type="submit" disabled={isSubmitting || (visibility === 'TAG_VISIBILITY_TEAM' && !teamID)}>
                {isSubmitting ? t('common.creating') : t('tag.create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
});

export default TagCreatePage;
